import React, { useEffect, useMemo, useState } from 'react'
import { SWRConfiguration } from 'swr'
import useSWRInfinite from 'swr/infinite'
import axios from 'axios'
import Cookies from 'js-cookie'
import { addDays, format, isToday, isValid, parseISO } from 'date-fns'
import { ArrowLineDown, ArrowCounterClockwise, PaperPlaneTilt, Repeat, X } from '@phosphor-icons/react'
import { CheckboxV2, Dialog, DialogContent, DialogOverlay, Icon, IconButton, Tooltip, TooltipContent, TooltipTrigger } from '@shopflo/ui'
import { apiURI, constants } from 'src/lib/utils/constants'
import { formattedFileSize, fromUTC } from 'src/lib/utils/helpers'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { normalizeReportRuns } from './parser'
import { Report, ReportRun } from './types'
import { formatReportSchedule } from './reportFormatters'
import ShareModal from './ShareModal'

const fetcher: SWRConfiguration['fetcher'] = async (url: string) => {
  const response = await axios.get(url, {
    baseURL: apiURI.CHECKOUT_NODE,
    headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
  })
  return response.data
}

const authHeaders = () => ({
  baseURL: apiURI.CHECKOUT_NODE,
  headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
})

const toDate = (value?: string): Date | null => {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

const formatGeneratedOn = (run: ReportRun): string => {
  const date = toDate(run.createdAt)
  if (!date) return '-'
  return isToday(date) ? 'Today' : format(date, 'MMM dd')
}

const formatCovers = (run: ReportRun): string => {
  const start = fromUTC(run.startDate)
  const endBoundary = fromUTC(run.endDate)
  const end = endBoundary ? addDays(endBoundary, -1) : null
  if (!start || !end) return '-'
  if (start.getMonth() === end.getMonth()) return `${format(start, 'd')}–${format(end, 'd MMM')}`
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM')}`
}

const formatSize = (run: ReportRun): string => {
  if (!run.fileSize || run.status === 'NO_DATA' || run.status === 'FAILED') return '-'
  return formattedFileSize(run.fileSize)
}

const RunRow: React.FC<{ run: ReportRun; selected: boolean; onToggle: () => void; onMutate: () => void }> = ({
  run,
  selected,
  onToggle,
  onMutate
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  const isFailed = run.status === 'FAILED'

  const handleDownload = async () => {
    try {
      setIsDownloading(true)
      const response = await axios.get(`/dashboard/report/download?runId=${run.runId}`, authHeaders())
      window.open(response.data.url, '_blank')
    } catch (error) {
      console.error('run download failed', error)
      errorToast('Failed to download report')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleRetry = async () => {
    try {
      setIsRetrying(true)
      await axios.post(`/dashboard/report/${run.runId}/retry`, {}, authHeaders())
      successToast('Report retried successfully')
      onMutate()
    } catch (error) {
      console.error('run retry failed', error)
      errorToast('Failed to retry report')
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <div
      className='group flex cursor-pointer items-center gap-2 rounded-lg px-5 py-2.5 transition-colors hover:bg-zinc-50'
      onClick={onToggle}
    >
      <span className='flex w-5 shrink-0 justify-center'>
        <CheckboxV2 className='pointer-events-none' checked={selected} size='sm' tabIndex={-1} aria-hidden />
      </span>
      <div className='flex flex-1 items-center gap-2'>
        <span className='text-text-sm-medium text-zinc-800'>{formatGeneratedOn(run)}</span>
        {isFailed && <span className='text-text-xs-medium text-amber-600'>Delivery failed</span>}
      </div>
      <span className='w-24 text-text-xs-regular text-zinc-500'>{formatCovers(run)}</span>
      <span className='w-20 text-text-xs-regular text-zinc-500'>{formatSize(run)}</span>
      <div className='flex w-9 items-center justify-end' onClick={e => e.stopPropagation()}>
        {isFailed ? (
          <IconButton
            IconComponent={ArrowCounterClockwise}
            size='sm'
            variant='outline'
            className='text-zinc-600'
            loading={isRetrying}
            onClick={handleRetry}
            aria-label='Retry'
          />
        ) : (
          <IconButton
            IconComponent={ArrowLineDown}
            size='sm'
            variant='ghost'
            className='text-zinc-600'
            loading={isDownloading}
            disabled={run.status !== 'COMPLETED'}
            onClick={handleDownload}
            aria-label='Download'
          />
        )}
      </div>
    </div>
  )
}

interface MultipleReportDialogProps {
  open: boolean
  onClose: () => void
  report: Report
}

const RUNS_PER_PAGE = 10

const MultipleReportDialog: React.FC<MultipleReportDialogProps> = ({ open, onClose, report }) => {
  const [selectedRuns, setSelectedRuns] = useState<Set<string>>(new Set())
  const [shareOpen, setShareOpen] = useState(false)

  const getKey = (pageIndex: number, previousPageData: any) => {
    // Note: intentionally NOT gated on `open` — dropping the key on close clears the SWR data and flashes
    // the "No runs yet" empty state during the close animation. Keeping the key preserves the rows till close.
    if (previousPageData && (previousPageData.reportDownloads?.length ?? 0) === 0) return null
    return `/dashboard/report/${report.reportId}/list?limit=${RUNS_PER_PAGE}&offset=${pageIndex * RUNS_PER_PAGE}`
  }

  const { data, size, setSize, isValidating, mutate } = useSWRInfinite(getKey, fetcher, { revalidateOnFocus: false })

  const reportRuns = useMemo(
    () => normalizeReportRuns(data?.flatMap((page: any) => page?.reportDownloads ?? []) ?? []),
    [data]
  )

  // Reset the selection whenever the dialog opens or the report changes.
  useEffect(() => {
    setSelectedRuns(new Set())
  }, [open, report.reportId])

  const allSelected = reportRuns.length > 0 && reportRuns.every(run => selectedRuns.has(run.runId))
  const someSelected = selectedRuns.size > 0

  const toggleRun = (runId: string) =>
    setSelectedRuns(prev => {
      const next = new Set(prev)
      next.has(runId) ? next.delete(runId) : next.add(runId)
      return next
    })

  const toggleAll = () => setSelectedRuns(allSelected ? new Set() : new Set(reportRuns.map(run => run.runId)))

  const lastPage: any = data?.[data.length - 1]
  const hasMore = !!lastPage && (lastPage.reportDownloads?.length ?? 0) === RUNS_PER_PAGE
  const isLoading = isValidating && size === 1 && reportRuns.length === 0
  const isLoadingMore = isValidating && size > 1

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (hasMore && !isLoadingMore && el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      setSize(size + 1)
    }
  }

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogOverlay className='fixed inset-0 z-40 bg-black/40' />
      <DialogContent className='z-50 !w-[600px] !max-w-[92vw] gap-0 overflow-hidden !rounded-2xl bg-white p-0 shadow-modal [&>button]:hidden'>
        <div className='flex flex-col'>
          {/* Header */}
          <div className='flex items-start justify-between px-5 pb-5 pt-5'>
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <span className='text-text-xs-medium text-zinc-500'>Run History</span>
              <h2 className='truncate text-text-md-semibold text-zinc-800' title={report.name}>
                {report.name.length > 40 ? `${report.name.slice(0, 40)}…` : report.name}
              </h2>
            </div>
            <div className='flex shrink-0 items-center gap-2 pl-3'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex'>
                    <IconButton
                      IconComponent={PaperPlaneTilt}
                      size='sm'
                      variant='ghost'
                      className='text-zinc-600'
                      disabled={!someSelected}
                      onClick={() => setShareOpen(true)}
                      aria-label='Send'
                    />
                  </span>
                </TooltipTrigger>
                {!someSelected && <TooltipContent align='start'>Select at least one report</TooltipContent>}
              </Tooltip>
              <div className='mx-1 h-4 w-px bg-zinc-200' />
              <IconButton IconComponent={X} size='sm' variant='ghost' onClick={onClose} />
            </div>
          </div>

          {/* Table */}
          <div className='flex flex-col'>
            <div className='flex items-center gap-2 border-b border-zinc-200 px-5 pb-2 px-2 mx-2'>
              <span className='flex w-5 shrink-0 justify-center'>
                <CheckboxV2
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  size='sm'
                  disabled={reportRuns.length === 0}
                  onCheckedChange={toggleAll}
                />
              </span>
              <span className='flex-1 text-text-xs-medium text-zinc-500'>Select all</span>
              <span className='w-24 text-text-xs-medium text-zinc-500'>Covers</span>
              <span className='w-20 text-text-xs-medium text-zinc-500'>Size</span>
              <span className='w-9' />
            </div>

            <div
              className='mt-1 flex max-h-[360px] flex-col overflow-y-auto overscroll-contain px-2'
              onScroll={handleScroll}
            >
              {isLoading ? (
                <div className='flex flex-col py-1'>
                  {[0, 1, 2].map(i => (
                    <div key={i} className='flex items-center gap-4 px-5 py-2.5'>
                      <div className='h-3 flex-1 animate-pulse rounded bg-zinc-100' />
                      <div className='h-3 w-24 animate-pulse rounded bg-zinc-100' />
                      <div className='h-3 w-20 animate-pulse rounded bg-zinc-100' />
                      <div className='h-3 w-9 animate-pulse rounded bg-zinc-100' />
                    </div>
                  ))}
                </div>
              ) : reportRuns.length === 0 ? (
                <div className='py-8 text-center text-text-sm-medium text-zinc-500'>No runs yet</div>
              ) : (
                <>
                  {reportRuns.map(run => (
                    <RunRow
                      key={run.runId}
                      run={run}
                      selected={selectedRuns.has(run.runId)}
                      onToggle={() => toggleRun(run.runId)}
                      onMutate={mutate}
                    />
                  ))}
                  {isLoadingMore && (
                    <div className='flex items-center gap-4 px-5 py-2.5'>
                      <div className='h-3 flex-1 animate-pulse rounded bg-zinc-100' />
                      <div className='h-3 w-24 animate-pulse rounded bg-zinc-100' />
                      <div className='h-3 w-20 animate-pulse rounded bg-zinc-100' />
                      <div className='h-3 w-9 animate-pulse rounded bg-zinc-100' />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer schedule */}
          <div className='flex items-center gap-2 border-zinc-200 px-5 py-5'>
            <Icon IconComponent={Repeat} size='md' className='shrink-0 text-zinc-500' iconProps={{ weight: 'duotone' }} />
            <span className='text-text-sm-regular text-zinc-500'>{formatReportSchedule(report)}</span>
          </div>
        </div>

        <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} runId={Array.from(selectedRuns)} />
      </DialogContent>
    </Dialog>
  )
}

export default MultipleReportDialog
